import { useEffect, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import ImageWithFallback from '../../../../components/ImageWithFallback/ImageWithFallback';
import { isStrongPassword } from '../../../../components/PasswordStrengthMeter/passwordStrength';
import { PasswordStrengthMeter } from '../../../../components/PasswordStrengthMeter/PasswordStrengthMeter';
import { useLanderBanner } from '../../../../hooks/useLanderBanner';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import type { OAuthPlatform } from '../../../../services/types';
import { useStores } from '../../../../stores/useStores';
import { devLog } from '../../../../utils/log';
import { emailValidator, stripNonAsciiPassword } from '../../../../utils/validators';
import { RecaptchaNotice } from '../../RecaptchaNotice/RecaptchaNotice';
import { useNewLanderSignUp } from '../../useNewLanderSignUp';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { useSignUpWizardContext } from '../../wizards/signUpWizard.provider';
import { useHandleSignUpWizardRedesign } from '../../wizards/useHandleSignUpWizardRedesign';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';
import { RedesignEmailAndPasswordInputs } from '../RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { ThirdPartyAuthRedesign } from '../ThirdPartyAuthRedesign/ThirdPartyAuthRedesign';

import styles from './SignUpOptionsRedesign.module.scss';
import { TermsAndServices } from './TermsAndServices';

export const SignUpOptionsRedesign = observer(function SignUpOptionsRedesign() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldsErrors, setFieldsErrors] = useState({ password: '', email: '' });
  const [bypassBlockSignupWithEmail, setBypassBlockSignupWithEmail] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [signupSessionId] = useState<string>(() => {
    const stored = localStorage.getItem('signup_session_id');
    if (stored) {
      localStorage.removeItem('signup_session_id');
      return stored;
    }
    return crypto.randomUUID();
  });
  const { t } = useTranslation();

  const { authStore, portalStore, landingStore } = useStores();
  const { isSigningUp } = authStore;

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const { isNewLanderSignUp: isNewLander, isNewEmLander: isEmLander } = useNewLanderSignUp();
  // direct_em follows the direct_reg layout: banner, fields, consents, Google only, OR, create account
  const { bannerUrl } = useLanderBanner('marketing', { enabled: isNewLander });
  const blockSignupWithEmailFeatureFlag = useFlag('block_signup_with_email', false);

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { handleSignUpWizard } = useHandleSignUpWizardRedesign();
  const { error, updateContextData } = useSignUpWizardContext();

  const clearError = () => updateContextData({ error: '' });

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.data === 'bypassBlockSignupWithEmail') {
        setBypassBlockSignupWithEmail(true);
      }
    };

    window.addEventListener('message', eventHandler);
    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, []);

  useEffect(() => {
    if (isNewLander) {
      landingStore.sendLandingPageEvent(isEmLander ? 'direct_em' : 'direct_su');
    }
  }, [isEmLander, isNewLander, landingStore]);

  useEffect(() => {
    const params: AnalyticsEvents['create_account'] = {
      event_action: 'impression',
      event_category: 'sign_up',
    };
    void analytics.sendEvent('create_account', params);

    devLog('create_account impression', { signup_session_id: signupSessionId });
  }, [signupSessionId]);

  const handleLogin = () => {
    const params: AnalyticsEvents['login_account_button'] = {
      event_action: 'click',
      event_category: 'sign_up',
      button_text: 'log in',
    };
    void analytics.sendEvent('login_account_button', params);
    openAuthWizard('logInWizard', {
      triggeredBy: 'sign_up_platform',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
  };

  const blockSignupWithEmail = blockSignupWithEmailFeatureFlag && !bypassBlockSignupWithEmail;

  const onTermsChange = (value: boolean) => {
    setIsTermsAccepted(value);
    if (value) {
      setApproveError('');
      void analytics.sendEvent('age_approve', {
        event_action: 'click',
        event_category: 'sign_up',
      });
    }
  };

  const validateApprovalFields = () => {
    if (!isTermsAccepted) {
      setApproveError(t('auth.approvalConditionsRequired'));
    } else {
      setApproveError('');
    }

    return isTermsAccepted;
  };

  const getPasswordError = () => {
    if (!password.trim()) {
      return t('auth.pleaseFillInAllRequiredFields');
    }

    return '';
  };

  const handleEmailSignUp = () => {
    const isValid = validateApprovalFields();

    if (!isValid) {
      return;
    }

    const emailError = email.trim() ? emailValidator(email.trim()) || '' : t('auth.pleaseFillInAllRequiredFields');
    const passwordError = getPasswordError();

    if (emailError || passwordError) {
      setFieldsErrors({ email: emailError, password: passwordError });
      updateContextData({ error: emailError || passwordError });
      return;
    }

    // The button stays disabled until the password is strong; this also stops an Enter-key submit.
    // OX Agent: Authentication vulnerability prevented by strong password policy
    if (!isStrongPassword(password)) {
      return;
    }

    updateContextData({ error: '' });

    devLog('create_account_by_email clicked', { signup_session_id: signupSessionId });

    handleSignUpWizard({ provider: 'email', credentials: { email, password }, action: 'sign_up' });
  };

  const onClose = () => {
    void analytics.sendEvent('create_account', {
      event_action: 'click',
      event_category: 'sign_up',
      click_location: 'close',
    });
    portalStore.closePortal();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isSigningUp || blockSignupWithEmail) {
      return;
    }

    const targetId = (event.target as HTMLElement)?.id;

    if (event.key === 'Enter' && (targetId === 'email' || targetId === 'password')) {
      event.preventDefault();
      handleEmailSignUp();
    }
  };

  const banner = isNewLander && bannerUrl && (
    <ImageWithFallback alt="" className={styles.landerBannerImg} src={bannerUrl} />
  );

  const thirdPartyAuth = (
    <div className={styles.thirdPartyAuthWrapper}>
      {!isEmLander && (
        <div className={styles.signUpOptionsSubtitleWrapper}>
          <span className={styles.signUpOptionsSubtitle}>{t('auth.signUpWith')}</span>
        </div>
      )}
      <ThirdPartyAuthRedesign
        action="Sign Up"
        signupSessionId={signupSessionId}
        onLoginAttempt={validateApprovalFields}
        {...(isEmLander && {
          excludedProviders: ['apple', 'facebook'] as OAuthPlatform[],
          classNameButtonContent: styles.directRegGoogleButtonContent,
          googleButtonClassName: styles.directRegGoogleButton,
          googleIconContainerClassName: styles.directRegGoogleIcon,
          googleLabel: t('account.signUpGoogle'),
        })}
      />
    </div>
  );

  const divider = (
    <div className={styles.rowLineContainer}>
      <div className={classNames(styles.lineGradient, styles.lineGradientLeft)} />
      <span className={styles.orEmailText}>{isEmLander ? t('common.or') : t('auth.orEmail')}</span>
      <div className={classNames(styles.lineGradient, styles.lineGradientRight)} />
    </div>
  );

  const emailAndPasswordInputs = (
    <>
      <RedesignEmailAndPasswordInputs
        className={styles.emailAndPasswordInputsWrapper}
        disabled={isSigningUp}
        emailError={fieldsErrors.email}
        hideErrors={true}
        password={password}
        passwordError={fieldsErrors.password}
        type="sign_up"
        onChangeEmail={(newEmail) => {
          clearError();
          setEmail(newEmail);
        }}
        onChangePassword={(newPassword) => {
          clearError();
          setFieldsErrors((prev) => ({ ...prev, password: '' }));
          setPassword(stripNonAsciiPassword(newPassword));
        }}
      />
      <PasswordStrengthMeter showHint className={styles.passwordStrengthMeter} password={password} />
    </>
  );

  const termsAndErrors = (
    <>
      <TermsAndServices isError={!!approveError} isTermsAccepted={isTermsAccepted} setIsTermsAccepted={onTermsChange} />

      <ErrorMessage className={styles.authErrorMessage}>
        {error || approveError || fieldsErrors.email || fieldsErrors.password}
      </ErrorMessage>
    </>
  );

  const createAccountButton = !blockSignupWithEmail && (
    <div className={styles.createAccountEmailButtonWrapper}>
      <Button
        btnType="main"
        className={styles.emailSignUpButton}
        dataTestId="signup-create-account-btn"
        // Gray until the password meets every rule in the checklist above.
        disabled={!isStrongPassword(password)}
        onClick={handleEmailSignUp}
      >
        {t('auth.createAccount')}
      </Button>
    </div>
  );

  return (
    <RedesignContainer className={styles.container} onKeyDown={handleKeyDown}>
      <DialogHeader
        className={styles.dialogHeader}
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="signup-back-button"
        headerLeftElement={<HeaderLogoElement position="left" showLogo={isDesktop} />}
        headerRightElement={<HeaderLogoElement position="right" showLogo={!isDesktop} />}
        title={isEmLander ? '' : t('auth.createAccount')}
        titleClassName={styles.dialogHeaderTitle}
        {...(isDesktop ? { onClose } : { onGoBack: onClose })}
      />
      <div className={styles.containerContent}>
        {banner}
        {isEmLander ? (
          <>
            {emailAndPasswordInputs}
            {termsAndErrors}
            {thirdPartyAuth}
            {!blockSignupWithEmail && divider}
            {createAccountButton}
          </>
        ) : (
          <>
            {thirdPartyAuth}
            {divider}
            {emailAndPasswordInputs}
            {termsAndErrors}
            {createAccountButton}
          </>
        )}

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="signup-redesign-already-have-account"
        >
          {/*
           * The title-casing stays in JS and arrives as a value. Baking the mutated result into the
           * key would change what renders, which is a separate change from this one.
           */}
          <Trans
            components={{
              login: <span className={styles.loginLink} data-testid="lobby-login-btn" onClick={handleLogin} />,
              question: <span />,
            }}
            i18nKey="auth.alreadyHaveAccountSentence"
            values={{ logIn: t('auth.logIn') }}
          />
        </div>
        <RecaptchaNotice />
      </div>
    </RedesignContainer>
  );
});
